import React from 'react';
import { BRAND } from '../../styles/brand';

export interface LockBarProps {
  status: 'open' | 'locked';
  onLock: () => void;
  onUnlock: () => void;
  lockedAt?: string | null;
  lockedByName?: string | null;
  canLock?: boolean;
  busy?: boolean;
  faseLabel?: string;
  /** Beheerdersknop om de sectie leeg te maken (testsessies). Alleen bij een open fase. */
  canClear?: boolean;
  onClear?: () => void;
  clearing?: boolean;
}

/**
 * De Lock — de kern van de Leene Omgevingscommunicatie Methode (uitgangspunt 5).
 * Na een fase valideren opdrachtgever en adviseur gezamenlijk het resultaat en leggen
 * het vast in het projectdossier. Een gelockte fase kan worden heropend en opnieuw
 * gevalideerd, zodat het dossier actueel blijft zonder eerdere keuzes te verliezen.
 */
export const LockBar: React.FC<LockBarProps> = ({
  status,
  onLock,
  onUnlock,
  lockedAt,
  lockedByName,
  canLock = true,
  busy = false,
  faseLabel = 'Deze fase',
  canClear = false,
  onClear,
  clearing = false,
}) => {
  const locked = status === 'locked';
  const formatted = lockedAt
    ? new Date(lockedAt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  return (
    <div
      style={{
        position: 'sticky',
        bottom: 0,
        marginTop: 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        padding: '24px 22px',
        borderRadius: 12,
        border: `1px solid ${locked ? BRAND.rand : '#e5e7eb'}`,
        background: locked ? BRAND.papier : '#ffffff',
        boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 34,
            height: 34,
            borderRadius: 9,
            background: locked ? BRAND.groen : '#eef1f6',
            color: locked ? '#fff' : '#6b7280',
            flexShrink: 0,
          }}
        >
          {locked ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 9.9-1" /></svg>
          )}
        </span>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: locked ? BRAND.groen : '#1a1a2e' }}>
            {locked ? `${faseLabel} is gevalideerd` : `${faseLabel} is nog open`}
          </div>
          <div style={{ fontSize: 12.5, color: '#6b7280' }}>
            {locked
              ? `Vastgelegd in het projectdossier${formatted ? ` op ${formatted}` : ''}${lockedByName ? ` door ${lockedByName}` : ''}.`
              : 'Valideer om vast te leggen in het projectdossier. Volgende fasen bouwen voort op de gevalideerde versie.'}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
      {canClear && onClear && (
        <button
          onClick={onClear}
          disabled={busy || clearing || locked}
          title={locked ? 'Heropen de fase eerst om leeg te maken' : 'Maak deze fase leeg om opnieuw te kunnen invullen'}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            padding: '10px 16px', borderRadius: 8, fontSize: 13.5, fontWeight: 600,
            border: '1px solid #f0c9c9', background: '#fff', color: '#b91c1c',
            cursor: busy || clearing || locked ? 'default' : 'pointer', opacity: busy || clearing || locked ? 0.5 : 1, whiteSpace: 'nowrap',
          }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>
          {clearing ? 'Leegmaken…' : 'Maak leeg'}
        </button>
      )}
      {locked ? (
        <button
          onClick={onUnlock}
          disabled={busy}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '10px 18px', borderRadius: 8, fontSize: 14, fontWeight: 600,
            border: '1px solid #cbd5e1', background: '#fff', color: '#334155',
            cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1, whiteSpace: 'nowrap',
          }}
        >
          Heropenen
        </button>
      ) : (
        <button
          onClick={onLock}
          disabled={busy || !canLock}
          title={!canLock ? 'Vul de fase eerst voldoende in' : undefined}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '10px 20px', borderRadius: 8, fontSize: 14, fontWeight: 600,
            border: 'none', background: canLock ? BRAND.groen : '#c7cbd4', color: '#fff',
            cursor: busy || !canLock ? 'default' : 'pointer', opacity: busy ? 0.7 : 1, whiteSpace: 'nowrap',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
          Valideren &amp; vastleggen
        </button>
      )}
      </div>
    </div>
  );
};
